import React, { useCallback, useEffect, useState } from "react";
import api, { errMsg } from "../../core/api";
import { useApp } from "../../core/AppContext";
import { fa, formatDate, todayIso } from "../../core/format";
import Modal from "../../components/Modal";
import JalaliDate from "../../components/JalaliDate";
import ResponsiveList from "../../components/ResponsiveList";
import FileViewer from "../../components/FileViewer";
import MultiFileField from "../../components/MultiFileField";
import PrintReport from "../../components/PrintReport";
import { violationPrint } from "../../core/printConfigs";

const sizeText = (b) =>
  b >= 1048576
    ? `${fa((b / 1048576).toFixed(1))} مگابایت`
    : `${fa(Math.max(1, Math.round(b / 1024)))} کیلوبایت`;
const fileIcon = (m) =>
  m === "application/pdf" ? "fa-regular fa-file-pdf" : "fa-regular fa-image";
const box = {
  whiteSpace: "pre-wrap",
  lineHeight: 2,
  background: "var(--card-2)",
  border: "1px solid var(--border)",
  borderRadius: 10,
  padding: "10px 14px",
};

const statusOf = (r, role) => {
  if (r.forwarded) return ["ارسال شد به مدیر بندر", "ok"];
  if (role === "admin") return ["جدید", "warn"];
  if (r.sentToAdmin) return ["ارسال شد به مسئول ایمنی", "wait"];
  return ["ارسال نشده (پیش‌نویس)", "bad"];
};
const Badge = ({ r, role }) => {
  const [t, c] = statusOf(r, role);
  return <span className={`badge ${c}`}>{t}</span>;
};

/* ---------- ثبت تخلف ---------- */
function CreateModal({ onClose, onDone }) {
  const { toast } = useApp();
  const [form, setForm] = useState({
    violationDate: todayIso(),
    subject: "",
    location: "",
    description: "",
    actions: "",
    sendToAdmin: true,
  });
  const [files, setFiles] = useState([]);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.violationDate) return setErr("تاریخ را انتخاب کنید");
    if (!form.subject.trim()) return setErr("موضوع تخلف را وارد کنید");
    if (!form.location.trim()) return setErr("محل انجام تخلف را وارد کنید");
    if (!form.description.trim()) return setErr("شرح تخلف را وارد کنید");
    setBusy(true);
    setErr("");
    try {
      const fd = new FormData();
      fd.append("violationDate", form.violationDate);
      fd.append("subject", form.subject.trim());
      fd.append("location", form.location.trim());
      fd.append("description", form.description.trim());
      fd.append("actions", form.actions.trim());
      fd.append("sendToAdmin", form.sendToAdmin ? "1" : "0");
      files.forEach((f) => fd.append("files", f));
      await api.post("/violations", fd, { timeout: 180000 });
      toast(
        form.sendToAdmin
          ? "تخلف ثبت و برای مسئول ایمنی ارسال شد"
          : "تخلف ثبت شد",
      );
      onDone();
    } catch (e) {
      setErr(errMsg(e));
      setBusy(false);
    }
  };

  return (
    <Modal
      title="ثبت تخلف"
      onClose={() => !busy && onClose()}
      footer={
        <>
          <button className="btn" onClick={submit} disabled={busy}>
            {busy ? "در حال ارسال..." : "ارسال"}
          </button>
          <button className="btn btn-ghost" onClick={onClose} disabled={busy}>
            انصراف
          </button>
        </>
      }
    >
      {err && <div className="alert err">{err}</div>}
      <JalaliDate
        label={
          <>
            تاریخ<span className="req">*</span>
          </>
        }
        value={form.violationDate}
        onChange={(v) => set("violationDate", v)}
      />
      <div className="field">
        <label>
          موضوع تخلف<span className="req">*</span>
        </label>
        <input
          value={form.subject}
          maxLength={250}
          onChange={(e) => set("subject", e.target.value)}
        />
      </div>
      <div className="field">
        <label>
          محل انجام تخلف<span className="req">*</span>
        </label>
        <input
          value={form.location}
          maxLength={200}
          onChange={(e) => set("location", e.target.value)}
        />
      </div>
      <div className="field">
        <label>
          شرح تخلف<span className="req">*</span>
        </label>
        <textarea
          rows={5}
          value={form.description}
          onChange={(e) => set("description", e.target.value)}
        />
      </div>
      <div className="field">
        <label>اقدامات انجام شده</label>
        <textarea
          rows={4}
          value={form.actions}
          onChange={(e) => set("actions", e.target.value)}
        />
      </div>
      <MultiFileField
        label="فایل‌ها / عکس‌ها (اختیاری)"
        files={files}
        onChange={setFiles}
      />
      <div className="field">
        <label
          className={`check-item ${form.sendToAdmin ? "on" : ""}`}
          style={{ margin: 0, display: "inline-flex" }}
        >
          <input
            type="checkbox"
            checked={form.sendToAdmin}
            onChange={(e) => set("sendToAdmin", e.target.checked)}
          />
          ارسال به مسئول ایمنی
        </label>
        {!form.sendToAdmin && (
          <div className="hint">
            بدون تیک، تخلف فقط برای خودتان ذخیره می‌شود و بعداً با دکمه‌ی
            «ارسال» می‌توانید آن را بفرستید.
          </div>
        )}
      </div>
    </Modal>
  );
}

/* ---------- جزئیات ---------- */
function DetailModal({ row, role, onClose }) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewer, setViewer] = useState(null);

  useEffect(() => {
    api
      .get(`/violations/${row.id}/files`)
      .then((r) => setFiles(r.data.items))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [row.id]);

  const items = [
    ["ثبت‌کننده", row.creatorName],
    ["تاریخ", formatDate(row.violationDate)],
    ["محل انجام تخلف", row.location],
    ["وضعیت", <Badge key="s" r={row} role={role} />],
  ];
  return (
    <>
      <Modal
        wide
        title={row.subject}
        onClose={() => !viewer && onClose()}
        footer={
          <button className="btn btn-ghost" onClick={onClose}>
            بستن
          </button>
        }
      >
        <div className="grid2">
          {items.map(([l, v]) => (
            <div className="field" key={l}>
              <label>{l}</label>
              <div>{v || "—"}</div>
            </div>
          ))}
        </div>
        <div className="field">
          <label>شرح تخلف</label>
          <div style={box}>{row.description}</div>
        </div>
        <div className="field">
          <label>اقدامات انجام شده</label>
          <div style={box}>{row.actions || "—"}</div>
        </div>
        <div className="field">
          <label>فایل‌ها</label>
          {loading ? (
            <div className="spinner sm" />
          ) : files.length === 0 ? (
            <div className="hint">فایلی پیوست نشده است.</div>
          ) : (
            <div className="file-list">
              {files.map((f) => (
                <div key={f.id} className="file-row">
                  <i className={fileIcon(f.mime)} />
                  <span className="fname" title={f.name}>
                    {f.name}
                  </span>
                  <small className="hint fsize">{sizeText(f.size)}</small>
                  <button
                    className="icon-btn"
                    title="مشاهده"
                    onClick={() =>
                      setViewer({
                        path: `/violations/${row.id}/files/${f.id}`,
                        name: f.name,
                      })
                    }
                  >
                    <i className="fa-regular fa-eye" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </Modal>
      {viewer && <FileViewer {...viewer} onClose={() => setViewer(null)} />}
    </>
  );
}

export default function Violations() {
  const { user, toast, confirm } = useApp();
  const role = user.role;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [create, setCreate] = useState(false);
  const [detail, setDetail] = useState(null);
  const [printRow, setPrintRow] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows((await api.get("/violations")).data.items);
    } catch (e) {
      toast(errMsg(e), "err");
    } finally {
      setLoading(false);
    }
  }, [toast]);
  useEffect(() => {
    load();
  }, [load]);

  const send = async (r) => {
    if (
      !(await confirm("این تخلف برای مسئول ایمنی ارسال شود؟", {
        okText: "ارسال",
        danger: false,
      }))
    )
      return;
    try {
      await api.post(`/violations/${r.id}/send`);
      toast("برای مسئول ایمنی ارسال شد");
      load();
    } catch (e) {
      toast(errMsg(e), "err");
    }
  };
  const forward = async (r) => {
    if (
      !(await confirm(
        "این تخلف با تمام مشخصات و نام ثبت‌کننده برای مدیر بندر ارسال شود؟",
        { okText: "ارسال به مدیر بندر", danger: false },
      ))
    )
      return;
    try {
      await api.post(`/violations/${r.id}/forward`);
      toast("برای مدیر بندر ارسال شد");
      load();
    } catch (e) {
      toast(errMsg(e), "err");
    }
  };
  const remove = async (r) => {
    if (!(await confirm("این تخلف حذف شود؟ این کار قابل بازگشت نیست."))) return;
    try {
      await api.delete(`/violations/${r.id}`);
      toast("حذف شد");
      load();
    } catch (e) {
      toast(errMsg(e), "err");
    }
  };

  const subtitle =
    role === "staff"
      ? "ثبت تخلف و ارسال آن برای مسئول ایمنی"
      : role === "admin"
        ? "تخلفات ثبت‌شده توسط کاربران؛ امکان ارسال برای مدیر بندر"
        : "تخلفاتی که مسئول ایمنی برای شما ارسال کرده است";

  return (
    <>
      <div className="page-head">
        <div>
          <h2>{role === "staff" ? "ثبت تخلف" : "تخلفات"}</h2>
          <p>{subtitle}</p>
        </div>
        {role === "staff" && (
          <button className="btn" onClick={() => setCreate(true)}>
            <i className="fa-solid fa-plus" /> ثبت تخلف
          </button>
        )}
      </div>

      <ResponsiveList
        loading={loading}
        rows={rows}
        empty="تخلفی ثبت نشده است"
        onRowClick={(r) => setDetail(r)}
        columns={[
          {
            key: "violationDate",
            label: "تاریخ",
            render: (r) => formatDate(r.violationDate),
          },
          {
            key: "subject",
            label: "موضوع تخلف",
            render: (r) => <b>{r.subject}</b>,
          },
          { key: "location", label: "محل" },
          ...(role !== "staff"
            ? [{ key: "creatorName", label: "ثبت‌کننده" }]
            : []),
          {
            key: "fileCount",
            label: "فایل",
            render: (r) =>
              r.fileCount ? (
                <span>
                  <i className="fa-solid fa-paperclip" /> {fa(r.fileCount)}
                </span>
              ) : (
                "—"
              ),
          },
          {
            key: "status",
            label: "وضعیت",
            render: (r) => <Badge r={r} role={role} />,
          },
        ]}
        mobileTitle={(r) => r.subject}
        mobileBadge={(r) => <Badge r={r} role={role} />}
        mobileMeta={(r) => [
          {
            icon: "fa-regular fa-calendar",
            label: "تاریخ",
            value: formatDate(r.violationDate),
          },
          { icon: "fa-solid fa-location-dot", label: "محل", value: r.location },
          ...(role !== "staff"
            ? [
                {
                  icon: "fa-solid fa-user",
                  label: "ثبت‌کننده",
                  value: r.creatorName,
                },
              ]
            : []),
        ]}
        actions={(r) => (
          <>
            <button
              className="icon-btn"
              title="چاپ گزارش"
              onClick={(e) => {
                e.stopPropagation();
                setPrintRow(r);
              }}
            >
              <i className="fa-solid fa-print" />
            </button>
            <button
              className="icon-btn"
              title="مشاهده"
              onClick={(e) => {
                e.stopPropagation();
                setDetail(r);
              }}
            >
              <i className="fa-regular fa-eye" />
            </button>
            {role === "staff" && !r.sentToAdmin && (
              <button
                className="btn btn-sm"
                onClick={(e) => {
                  e.stopPropagation();
                  send(r);
                }}
              >
                <i className="fa-solid fa-paper-plane" /> ارسال
              </button>
            )}
            {role === "admin" && !r.forwarded && (
              <button
                className="btn btn-sm"
                onClick={(e) => {
                  e.stopPropagation();
                  forward(r);
                }}
              >
                <i className="fa-solid fa-share" /> ارسال به مدیر بندر
              </button>
            )}
            {((role === "staff" && !r.sentToAdmin) || role === "admin") && (
              <button
                className="icon-btn danger"
                title="حذف"
                onClick={(e) => {
                  e.stopPropagation();
                  remove(r);
                }}
              >
                <i className="fa-solid fa-trash" />
              </button>
            )}
          </>
        )}
      />

      {create && (
        <CreateModal
          onClose={() => setCreate(false)}
          onDone={() => {
            setCreate(false);
            load();
          }}
        />
      )}
      {detail && (
        <DetailModal row={detail} role={role} onClose={() => setDetail(null)} />
      )}
      {printRow && (
        <PrintReport
          {...violationPrint(printRow)}
          onClose={() => setPrintRow(null)}
        />
      )}
    </>
  );
}
